import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { useT } from "../i18n";

function fmtUsd(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

function fmtNative(amount, currency) {
  if (currency === "XTR") return `${Math.round(amount)} ⭐`;
  return `${Number(amount).toFixed(2)} ${currency}`;
}

export default function AffTransactions() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/affiliate/transactions?limit=200")
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const totalUsd = items.reduce((s, t2) => s + (t2.commission_amount_usd || 0), 0);

  return (
    <div className="space-y-5" data-testid="aff-transactions-page">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">{t("tx.title")}</h1>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">
            {t("tx.subtitle")}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
            {t("tx.total")}
          </div>
          <div className="text-xl font-semibold tabular-nums">{fmtUsd(totalUsd)}</div>
        </div>
      </header>

      <div className="border border-border bg-secondary/10 px-4 py-3 text-xs text-muted-foreground">
        {t("tx.tip")}
      </div>

      <div className="border border-border" data-testid="aff-transactions-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/30 text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left px-3 py-2">{t("tx.col.datetime")}</th>
              <th className="text-left px-3 py-2">{t("tx.col.user")}</th>
              <th className="text-right px-3 py-2">{t("tx.col.payment")}</th>
              <th className="text-right px-3 py-2">{t("tx.col.usd")}</th>
              <th className="text-right px-3 py-2">{t("tx.col.commission")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="5" className="px-3 py-6 text-center text-muted-foreground text-xs">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="5" className="px-3 py-8 text-center text-muted-foreground text-xs">
                  {t("tx.empty")}
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr
                key={row.id}
                className="border-b border-border hover:bg-secondary/20"
                data-testid={`aff-txn-${row.id}`}
              >
                <td className="px-3 py-2 font-mono text-xs">
                  {row.created_at.slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-3 py-2 font-mono">{row.user_label}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmtNative(row.payment_amount_native, row.payment_currency)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {fmtUsd(row.payment_amount_usd)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-400">
                  {fmtUsd(row.commission_amount_usd)}{" "}
                  <span className="text-muted-foreground text-xs">
                    ({row.commission_pct}%)
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
